import { Component, ErrorInfo, ReactNode } from 'react';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('error-boundary');

/**
 * Minimal error boundary.
 *
 * Deliberately hand-rolled rather than pulling in `react-error-boundary`
 * (which topic 09's plan suggested), for three reasons worth stating so this
 * isn't "reinvented by accident":
 *
 * 1. The API surface actually needed here is two React lifecycle methods.
 *    `getDerivedStateFromError` + `componentDidCatch` is the whole feature;
 *    there is no subtle behaviour a dependency would get right for us.
 * 2. This is a white-label product. Every fallback is tenant-branded, so the
 *    render path is bespoke regardless of who owns the catching logic.
 * 3. Topic 10 is a dependency-diet topic and the React 19 migration (also
 *    topic 09) is still pending -- one fewer package with a `react` peer
 *    range is one fewer thing to re-verify then. `.npmrc` sets
 *    `legacy-peer-deps=true`, so a bad peer range would install silently.
 *
 * Swapping in `react-error-boundary` later is a contained change: the props
 * below are deliberately a subset of its own (`fallback`, `onError`,
 * `resetKeys`).
 */
export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { error: null, resetKeys: undefined };

	static getDerivedStateFromError(
		error: Error,
	): Pick<ErrorBoundaryState, 'error'> {
		return { error };
	}

	/**
	 * Recover automatically when the thing that failed changes -- the reader
	 * swipes to a different article, or navigates elsewhere. Without this a
	 * boundary latches: error state survives a re-render with new props, so
	 * the fallback would stay put after the reader has moved on.
	 *
	 * Done here rather than in `componentDidUpdate` on purpose: clearing the
	 * error during the render pass avoids the extra commit-then-rerender that
	 * `setState`-in-`componentDidUpdate` causes (and that oxlint's
	 * `react/no-did-update-set-state` rightly flags). This is the same
	 * approach `react-error-boundary` takes.
	 */
	static getDerivedStateFromProps(
		props: ErrorBoundaryProps,
		state: ErrorBoundaryState,
	): ErrorBoundaryState | null {
		const { resetKeys } = props;
		if (!state.error) {
			// Track the current keys even while healthy, so the comparison
			// after a future error is against the keys at throw time.
			return hasChanged(state.resetKeys, resetKeys)
				? { ...state, resetKeys }
				: null;
		}
		return hasChanged(state.resetKeys, resetKeys)
			? { error: null, resetKeys }
			: null;
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Topic 16 owns real error reporting; until it lands this at least
		// makes the crash visible in the console (and to LogRocket, which
		// captures console output) instead of vanishing behind the fallback.
		logger.error(
			`[ErrorBoundary${this.props.label ? ` ${this.props.label}` : ''}]`,
			error,
			errorInfo.componentStack,
		);
		this.props.onError?.(error, errorInfo);
	}

	private readonly reset = () => {
		this.setState((state) => ({ ...state, error: null }));
	};

	render() {
		const { error } = this.state;
		if (!error) {
			return this.props.children;
		}
		const { fallback } = this.props;
		if (typeof fallback === 'function') {
			return fallback(error, this.reset);
		}
		return fallback ?? null;
	}
}

export interface ErrorBoundaryProps {
	children: ReactNode;
	/**
	 * Rendered instead of `children` once a descendant throws. A function
	 * receives the error and a `reset` callback that clears the boundary and
	 * re-attempts the subtree. Omit entirely to render nothing -- the right
	 * choice for a non-essential embed inside an article, where an empty gap
	 * beats a broken frame or a crashed page.
	 */
	fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
	/** Clears the error whenever any entry changes identity. */
	resetKeys?: ReadonlyArray<unknown>;
	/** Prefixes the console output, so nested boundaries are tellable apart. */
	label?: string;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
	error: Error | null;
	resetKeys: ReadonlyArray<unknown> | undefined;
}

function hasChanged(
	prev: ReadonlyArray<unknown> | undefined,
	next: ReadonlyArray<unknown> | undefined,
): boolean {
	if (prev === next) return false;
	if (!prev || !next) return prev !== next;
	return (
		prev.length !== next.length ||
		next.some((key, i) => !Object.is(key, prev[i]))
	);
}
